import React, { useState, useRef, useLayoutEffect } from 'react';
import { Divider, Stack, Typography, Button, useMediaQuery } from '@mui/material';
import PhysicianBars from './PhysicianBars';
import { theme } from '../../../theme';
import Filter from './KeyFilterComponents/Filter';
import { useLogUserActionMutation } from '../../../api/physicianDashboardSlice';
import { useNavigate } from 'react-router-dom';
import ToggleButtons from '../../CustomComponents/ToggleButtons';
import { getCategoriesAndColors, prepareCardsWithEntries, sortCardsByItemCost, calculateMaxTotalsForFilteredCategories, getFilteredCategoriesText } from '../../../utils/cardComparisonCalculations';

function PeerDiscoveryGraph({ analysisData, cardId, draftId }) {
    const navigate = useNavigate();
    const COLORS = Object.values(theme.palette.graphColors);
    const [peerReviewToggle, setPeerReviewToggle] = useState('card_items');
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterCategories, setFilterCategories] = useState([]);
    const graphRef = useRef(null);
    const [graphHeight, setGraphHeight] = useState(null);
    const [logUserAction] = useLogUserActionMutation();

    // Grab the cards (with comparison data attached)
    const cards = analysisData.cardComparisons;
    const dataKey = peerReviewToggle === 'supply_usage' ? 'usage_data' : 'item_cost_data';

    // Get categories and color mapping using utility
    const { uniqueCategories, categoryColorMap } = getCategoriesAndColors(cards, COLORS);
    
    // Prepare cards with filtered entries using utility
    const preparedCards = prepareCardsWithEntries(cards, dataKey, filterCategories);
    
    // Separate physician's card from peers (specific to physician dashboard)
    const physicianCard = preparedCards.filter(card => card.id === cardId);
    const peerCards = sortCardsByItemCost(
        preparedCards.filter(card => card.id !== cardId)
    );
    
    // Calculate max totals using utility
    const { maxUsageTotal, maxItemCostTotal } = calculateMaxTotalsForFilteredCategories(cards, filterCategories);
    const maxTotal = peerReviewToggle === 'supply_usage' ? maxUsageTotal : maxItemCostTotal;

    const toggleText = peerReviewToggle === 'supply_usage' ? 'Usage' : 'Card Supply Cost';
    const filteredCategoriesText = getFilteredCategoriesText(filterCategories, uniqueCategories);
    const isFiltered = filterCategories.length > 0 && filterCategories.length < uniqueCategories.length;

    // Measure graph height and pass it to Filter so the sidebar can be constrained to the same height.
    useLayoutEffect(() => {
        const updateHeight = () => {
            const h = graphRef.current?.getBoundingClientRect().height;
            if (h != null) setGraphHeight(Math.round(h));
        };

        updateHeight();
        let ro;
        if (window.ResizeObserver) {
            ro = new ResizeObserver(updateHeight);
            graphRef.current && ro.observe(graphRef.current);
        } else {
            window.addEventListener('resize', updateHeight);
        }

        return () => {
            ro?.disconnect?.();
            if (!window.ResizeObserver) window.removeEventListener('resize', updateHeight);
        };
    }, [peerReviewToggle, filterCategories, analysisData]);

    function handleBarClick(peerId) {
        if (filterCategories.length == 1) {
            navigate(`/physician/drafts/${draftId}/comparison?category=${filterCategories[0]}`, { state: { selectedPeer: peerId } });
        } else {
            navigate(`/physician/drafts/${draftId}/comparison`, { state: { selectedPeer: peerId } });
        }
        logUserAction({ action: 'viewed_peer_comparison', data: { peerId } });
    }

    return (
        <Stack className='pendo-peer-discovery-graph' direction='row' spacing={2} width={'100%'} justifyContent={'center'} alignItems={'stretch'}>
            {/* Graph */}
            <Stack ref={graphRef} spacing={2} padding={1.5} sx={{ backgroundColor: theme.palette.background.white, borderRadius: 1, boxShadow: 2, width: '100%', height: 'fit-content' }}>
                <ToggleButtons
                    options={[ { label: 'Card Items', value: 'card_items' }, { label: 'Supply Usage', value: 'supply_usage' } ]}
                    toggleValue={peerReviewToggle}
                    setToggleValue={setPeerReviewToggle}
                    logUserAction={logUserAction}
                    actionName="viewed_analysis_graph_tab"
                    actionLocation='physician_dashboard_peer_discovery'
                />
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Stack>
                        { peerReviewToggle === 'supply_usage' ? (
                            <Typography variant="normal" fontSize={'14px'}> Average Used Cost per Case </Typography>
                        ) : (
                            <Typography variant="normal" fontSize={'14px'}> Card Supply Cost </Typography>
                        )}
                        <Typography variant="normal" fontSize={'12px'} > {filteredCategoriesText} </Typography>
                    </Stack>
                    {isSmallScreen ? (                
                        <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-end' }} onClick={() => setFilterOpen(true)}> Key & Filter </Button>
                    ) : null}
                </Stack>
                <Divider />

                {/* Physician's bar */}
                <Typography variant="normal" fontSize={'14px'}> Your {toggleText} </Typography>
                <PhysicianBars cards={physicianCard} maxTotal={maxTotal} isPhysician={true} categoryColorMap={categoryColorMap} />

                {/* Peer's bars */}
                <Stack spacing={1}>
                    <Typography variant="normal" fontSize={'14px'}>Peer {toggleText}</Typography>
                    <Divider />
                    <PhysicianBars cards={peerCards} maxTotal={maxTotal} categoryColorMap={categoryColorMap} isFiltered={isFiltered} handleBarClick={handleBarClick} />
                </Stack>
            </Stack>
            {/* Filter drawer or sidebar */}
            <Filter
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                categories={uniqueCategories}
                selectedCategories={filterCategories}
                setSelectedCategories={setFilterCategories}
                categoryColorMap={categoryColorMap}
                showClose={isSmallScreen}
                graphHeight={graphHeight}
                logUserAction={logUserAction}
                logActionLocation='physician_dashboard_peer_discovery'
            />
        </Stack>
    );
}

export default PeerDiscoveryGraph;